/**
 * Universal Link (iOS) / App Link (Android) deep-link capability (src/platform/).
 *
 * Contract: every capability has a working web implementation; native
 * branches load Capacitor plugins dynamically and MUST keep the web
 * fallback. On web this is a complete no-op — a `https://app.maxflow.io/...`
 * link already opens the SPA directly, there is no separate shell to route
 * into.
 *
 * Companion to `oauthReturn.ts` (the `io.maxflow.app://` custom-scheme OAuth
 * return) — this module covers the second deep-link path: an ordinary
 * `https://app.maxflow.io/...` link (from push or email) that the OS hands to
 * the installed app instead of opening it in a browser, once iOS's
 * associated-domains entitlement / Android's autoVerify intent-filter match
 * (see `App.entitlements`, `AndroidManifest.xml`, and the served
 * `/.well-known/apple-app-site-association` + `assetlinks.json`).
 */

import type { PluginListenerHandle } from '@capacitor/core'

import { isNativePlatform } from './detection'

/** The associated domain declared in App.entitlements / AndroidManifest.xml. */
export const UNIVERSAL_LINK_HOST = 'app.maxflow.io'

/**
 * Map an incoming `https://app.maxflow.io/...` URL to an in-app route.
 *
 * Returns null for anything on a different host/protocol (the OAuth
 * custom-scheme return, share-target intents, future schemes) so the caller
 * leaves those alone. The mobile app mirrors the web app's routes 1:1, so the
 * path/search/hash carry over unchanged.
 */
export function universalLinkRoute(url: string): string | null {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }
  if (parsed.protocol.toLowerCase() !== 'https:') return null
  if (parsed.host.toLowerCase() !== UNIVERSAL_LINK_HOST) return null
  return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/'
}

/**
 * Route Universal/App Link deep links to `navigate` for as long as the
 * returned disposer is not called. No-op (and resolves to a no-op disposer)
 * on web.
 *
 * Covers both delivery paths: `appUrlOpen` when the app is already running,
 * and `getLaunchUrl()` for the cold-start case where the OS started the app
 * from the link and the listener was registered too late to see the event.
 */
export async function initUniversalLinkRouting(
  navigate: (route: string) => void
): Promise<() => void> {
  if (!isNativePlatform()) return () => undefined

  const go = (url: string | undefined) => {
    const route = url ? universalLinkRoute(url) : null
    if (route) navigate(route)
  }

  try {
    const { App } = await import('@capacitor/app')
    const handle: PluginListenerHandle = await App.addListener('appUrlOpen', ({ url }) => go(url))
    go((await App.getLaunchUrl())?.url)
    return () => void handle.remove()
  } catch {
    return () => undefined
  }
}
